/* eslint-disable @typescript-eslint/ban-types */

import * as Sentry from '@sentry/browser';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import { useLocation } from 'react-router-dom';

import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { ProviderType } from 'src/modules/user/models/user';

import { IntakeFormSaveForLaterModal } from '../components/IntakeFormSaveForLaterModal';
import { CoachMoreInfoModal } from 'src/modules/providers/components/CoachMoreInfoModal';
import { useGetIntakeFormInfoQuery } from '../documents/getIntakeFormInfoQuery.generated';
import { useUpdateIntakeFormMutation } from '../documents/updateIntakeFormMutation.generated';
import { IntakeFormProps, screenComponentTypes, Screens } from '../models/intakeForm';
import {
    getBulkCamelForIds,
    getCamelCaseId,
    getIntakeFormData,
    getNextScreen,
    getPreviousScreen,
} from '../selectors/intakeFormSelectors';

const screenCount = Screens.__LENGTH - 2;

type Props = RouteComponentProps;

export const IntakeFormPage: React.FC<Props> = props => {
    const { history, match } = props;
    const location = useLocation<{ uuid?: string }>();

    const coachAssignmentUuid: string =
        get(match, 'params.coachingAssignmentUuid', '') || location.state?.uuid;

    // This optional url path param indicates that we want to show the user their intake form, even
    // though they've already finished and submitted it.
    const reviewIntent = get(match, 'params.reviewIntent') === 'review';

    const [navigateToDashboard, setNavigateToDashboard] = useState(false);
    const [abandonIntent, setAbandonIntent] = useState(false);
    const [abandonedResponse, setAbandonedResponse] = useState<string | object>('');
    const [currentScreen, setCurrentScreen] = useState(Screens.Loading);
    const [errorMessage, setErrorMessage] = useState('');
    const [coachMoreInfoModal, setCoachMoreInfoModal] = useState(false);

    const [provider, setProvider] = useState({
        avatar: '',
        firstName: '',
        lastName: '',
        introduction: '',
        providerType: '',
    });
    const [intakeFormUuid, setIntakeFormUuid] = useState('');

    const userFirstName = useSelector(getUserInformation).firstName ?? '';

    const storedResponses = useRef({});

    const { data, loading } = useGetIntakeFormInfoQuery({
        fetchPolicy: 'network-only',
        variables: { coachAssignmentUuid },
    });
    const [updateIntakeForm] = useUpdateIntakeFormMutation();

    const changeCurrentScreen = (next: Screens) => {
        setCurrentScreen(next);
    };

    const saveResponse = async (
        questionId?: string,
        response?: string | string[],
        submitToCoach?: boolean,
    ) => {
        const lastScreen = currentScreen;

        if (!questionId) {
            skipQuestion();
            return;
        }

        changeCurrentScreen(Screens.Loading);
        storedResponses.current[lastScreen] = response;

        try {
            await updateIntakeForm({
                variables: {
                    intakeFormFormInput: { [getCamelCaseId(questionId)]: response },
                    intakeFormUuid,
                    submitToCoach,
                },
            });
            setErrorMessage('');
            changeCurrentScreen(getNextScreen(lastScreen));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            err.name = 'Error Saving Intake Form Response';
            Sentry.captureException(err);
            setErrorMessage('There was a problem saving your response.');
            changeCurrentScreen(lastScreen);
        }
    };

    const saveMultipleResponses = async (responseMap: object, submitToCoach?: boolean) => {
        const lastScreen = currentScreen;
        const variables = {
            intakeFormFormInput: getBulkCamelForIds(responseMap),
            intakeFormUuid,
            submitToCoach,
        };

        changeCurrentScreen(Screens.Loading);
        storedResponses.current[lastScreen] = responseMap;

        try {
            await updateIntakeForm({ variables });
            setErrorMessage('');

            if (lastScreen < screenCount) {
                changeCurrentScreen(getNextScreen(lastScreen));
            } else {
                history.push('/dashboard');
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            err.name = 'Error Bulk Saving Intake Form Responses';
            Sentry.captureException(err);
            setErrorMessage('There was a problem saving your response.');
            changeCurrentScreen(lastScreen);
        }
    };

    const skipQuestion = () => {
        changeCurrentScreen(getNextScreen(currentScreen));
    };

    const goBack = () => {
        changeCurrentScreen(getPreviousScreen(currentScreen));
    };

    const onAbandonClick = (response: string | object) => {
        setAbandonedResponse(response);
        setAbandonIntent(true);
    };

    const cancelAbandon = () => {
        setAbandonedResponse('');
        setAbandonIntent(false);
    };

    const toggleCoachMoreInfo = () => {
        setCoachMoreInfoModal(!coachMoreInfoModal);
    };

    useEffect(() => {
        if (loading) {
            return changeCurrentScreen(Screens.Loading);
        }

        if (!data || !data.coachAssignments) {
            return changeCurrentScreen(Screens.Loading);
        }

        if (isEmpty(data.coachAssignments) || !data.coachAssignments[0]) {
            return history.push('/dashboard');
        }

        const { coach, intakeForm } = data.coachAssignments[0];

        if (!coach || !intakeForm) {
            return history.push('/dashboard');
        }

        const { submitted, uuid: receivedIntakeFormUuid, intakeFormQuestionResponses } = intakeForm;
        const { lastScreen, answerData } = getIntakeFormData(intakeFormQuestionResponses);

        if (submitted && !reviewIntent) {
            return setNavigateToDashboard(true);
        }

        storedResponses.current = {
            ...storedResponses.current,
            ...answerData,
        };

        setProvider({
            ...coach,
            avatar: coach?.avatar || '',
            firstName: coach?.firstName || '',
            lastName: coach?.lastName || '',
            introduction: coach?.introduction || '',
            providerType: coach?.providerType || ProviderType.COACH,
        });

        setIntakeFormUuid(receivedIntakeFormUuid);
        setCurrentScreen(getNextScreen(lastScreen));
    }, [data, loading]);

    const ScreenComponent = screenComponentTypes[currentScreen];

    const intakeFormComponentProps: IntakeFormProps = {
        saveResponse,
        saveMultipleResponses,
        skipQuestion,
        goBack,
        onAbandonClick,
        coachAvatar: provider?.avatar,
        coachFirstName: provider?.firstName,
        coachLastName: provider?.lastName,
        coachCredentials: provider.providerType,
        userFirstName,
        currentScreen,
        errorMessage,
        responses: storedResponses.current[currentScreen] || null,
        screenCount,
        toggleCoachMoreInfo,
        allResponses: storedResponses.current,
    };

    const abandonModalProps = {
        cancelAbandon,
        abandonedResponse,
        currentScreen,
        saveResponse,
        saveMultipleResponses,
    };

    const coachMoreInfoModalProps = {
        coach: provider,
        closeCoachMoreInfoModal: toggleCoachMoreInfo,
    };

    if (!coachAssignmentUuid) {
        Sentry.captureException(
            new Error('Routed to intake form without a coaching assignment uuid in the url'),
        );

        return <Redirect to="/dashboard" />;
    }

    if (navigateToDashboard) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <>
            {!!abandonIntent && <IntakeFormSaveForLaterModal {...abandonModalProps} />}
            <CoachMoreInfoModal isVisible={!!coachMoreInfoModal} {...coachMoreInfoModalProps} />
            <ScreenComponent {...intakeFormComponentProps} />
        </>
    );
};
